<script setup lang="ts">
/**
 * The query input of a text query: what is typed becomes chips — one per
 * term — and every chip says how its term is searched (required, excluded or
 * one of an any-of group; contains, starts with, ends with or as a phrase;
 * case counts or not). The text rules, the wildcard parser included, are
 * `lib/textQuery`'s; this is the field around them.
 *
 * `v-model` is the list of terms (`TextQueryTerm[]`), `v-model:draft` the text
 * that is not a term yet — a consumer may search it live with `parse`. Enter
 * parses the whole draft: the terms are appended with their ids, the text that
 * could not be searched stays in the draft, and `issues` reports it (an empty
 * list when nothing was wrong) — under the field as „nicht gesucht: …“. A paste
 * with white space or a wildcard in it is parsed at once, the same way (not
 * while an input method composes: then it is inserted like any text). The
 * field never commits on blur, and an Enter on an empty draft does nothing.
 * The ids of the terms are `tq-<n>`, none that the model already holds.
 * `parse` is told how much room the query has left (`MAX_TERMS` minus the
 * terms): what does not fit stays in the draft with its issue.
 *
 * A leading minus always means „not“. Each one that did shows how it was read —
 * „verstanden als NICHT „PVC“ · stattdessen „-PVC“ suchen“ — and one click
 * swaps the term for the literal reading, keeping its id.
 *
 * `interaction` is how a chip is changed: `direct` (default) cycles on a click
 * on the chip's head (occurrence), match ending (the four modes) and „Aa“
 * (case), removes with ×, and opens the menu from a click on the term; `menu`
 * opens it from a click anywhere on the chip. Either way a selected chip opens
 * the same menu with Enter or Space. `wording` is how the head
 * speaks: `logic` (UND / NICHT / ODER) or `plain` (muss / ohne / eins von).
 * Both are the consumer's to bind from the settings; the field has none of its
 * own. The terms of the any-of group are shown together, „eins von ( … oder …
 * )“, where the first of them stands.
 *
 * `suggestion` shows a „Meinten Sie …“ row for one term. The field does **not**
 * change the term: it emits `accept-suggestion` (`termId`, `text`, `generation`
 * — the generation of the query the suggestion was made for, so a late answer
 * cannot hit a newer one), `ignore-suggestion` (`permanent: false` for „so
 * lassen“, `true` for „„X“ ist richtig“), and the parent decides, replaces the
 * text and clears `suggestion`. With `neverSuggest` (opt-in, default off) the
 * row has a fourth link, „„X“ nicht mehr vorschlagen“: it emits
 * `never-suggest` with the same binding as `accept-suggestion` (`termId`,
 * `text` — the suggested word —, `generation`); the parent stores it and
 * clears `suggestion`. `suggestion.confirmable: false` (a synonym, no spelling)
 * hides „„X“ ist richtig“. `fieldChips` are filters from elsewhere shown
 * in the same flow; × emits `remove-field`.
 *
 * `clearable` (default on) shows an × at the right end of the field while
 * there are terms, field chips or typed text — the same trailing place as
 * `NxSearchInput`. A click clears the terms and the draft, emits `clear` (the
 * caller drops `fieldChips` on that; the field does not) and puts the focus
 * back in the text. The live region then says „Suche geleert“. `:clearable="false"`
 * hides the ×. The button is its own tab stop, after the text field.
 *
 * Accessibility: a listbox of options with `aria-activedescendant` — the text
 * field keeps the focus, the chips are the options (`role="listbox"` /
 * `role="option"`; the name of an option is „<term>, <occurrence>, <match>[,
 * genau so geschrieben][, deaktiviert]“). Left at caret 0 or Backspace selects
 * the last chip; Left / Right / Home / End move; Backspace / Delete remove;
 * Enter / Space open its menu (Escape closes it and gives the focus back to the
 * field); `-` makes it an exclusion, `m` cycles the match, `a` toggles case —
 * those three only while a chip is selected, else they type. A click in the
 * text hands the keys back to it: no chip stays selected and no menu stays
 * open. So does selecting in the text: while a range of it is selected every
 * key is the text's (Backspace deletes the text, never a chip behind it), and
 * a key that works on it — Ctrl, Cmd or Alt with it, Shift with an arrow, Up,
 * Down, Page Up, Page Down — ends the chip selection like typing does; a field
 * without text has nothing to work on, there the chip keys stay. A menu closes
 * when the focus leaves it for another control; its arrow keys choose a mode
 * at once and Escape ends it. A chip that moves (its term joins or
 * leaves the any-of group) or goes keeps the focus where it was: on the same
 * control, or in the text. While an input method composes (and for 80 ms
 * after) no key is taken and no paste split. A visually hidden polite live
 * region announces `status` (the result count, from the consumer) with the
 * issues, the minus readings and the suggestion, after a short pause and only
 * when the text changes. Clearing the field adds „Suche geleert“ to that
 * announcement, once; a later count does not repeat it.
 *
 * The texts are props with German defaults; `NX_QUERY_INPUT_LABELS_EN` is the
 * English set.
 */
import { DismissIcon } from '@blpde/nx-icons';
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onBeforeUpdate,
  onUpdated,
  ref,
  useId,
  watch,
} from 'vue';
import { NxButton } from '../Button';
import { NxChip } from '../Chip';
import { NxIconButton } from '../IconButton';
import { useBrowserAutofillOffAttrs } from '../Inputs/browserAutofill';
import { labelInjectionKey } from '../Inputs/internal';
import NxTagField from '../Inputs/NxTagField.vue';
import {
  MAX_TERMS,
  hasWhiteSpace,
  parse,
  trimWhiteSpace,
  type Match,
  type MinusReading,
  type Occur,
  type ParseIssue,
  type TextQueryTerm,
} from '../lib/textQuery';
import { NxSuggestionRow } from '../SuggestionRow';
import NxQueryChip from './NxQueryChip.vue';
import {
  NX_QUERY_INPUT_LABEL_KEYS,
  NX_QUERY_INPUT_LABELS_DE,
  fillTemplate,
  issueText,
  occurText,
  type NxQueryInputLabels,
  type NxQueryInputTexts,
  type NxQueryInteraction,
  type NxQueryWording,
} from './queryInputLabels';
import { nextMatch, withCaseSensitive, withDisabled, withMatch, withOccur } from './queryTerm';

/** A filter from elsewhere, shown as a removable chip next to the terms. */
export interface NxQueryFieldChip {
  id: string;
  label: string;
}

/** A spelling suggestion for one term, and the answer that takes it over. */
export interface NxQuerySuggestion {
  /** The term the suggestion is for. */
  termId: string;
  /** The suggested spelling. */
  text: string;
  /** The generation of the query it was made for. */
  generation: number;
  /**
   * Default `true`. `false` takes „„X“ ist richtig“ away: the suggestion is no
   * spelling of the term (a synonym). The answers do not carry it.
   */
  confirmable?: boolean;
}

/** „Ignorieren“ (`permanent: false`) or „„X“ ist richtig“ (`permanent: true`). */
export interface NxQueryIgnoreSuggestion {
  termId: string;
  generation: number;
  permanent: boolean;
}

const props = withDefaults(
  defineProps<
    NxQueryInputLabels & {
      /** How a chip is changed: on the chip (`direct`) or in a menu. Default `direct`. */
      interaction?: NxQueryInteraction;
      /** How the head of a chip speaks: `logic` (UND / NICHT / ODER) or `plain` (muss / ohne / eins von). Default `logic`. */
      wording?: NxQueryWording;
      /** Filters from elsewhere, shown after the terms; × emits `remove-field`. */
      fieldChips?: readonly NxQueryFieldChip[];
      /**
       * Trailing × while there are terms, field chips or typed text. A click
       * clears the terms and the draft, emits `clear` and focuses the text.
       * The caller drops `fieldChips` on `clear`. Default on;
       * `:clearable="false"` hides it.
       */
      clearable?: boolean;
      /** A „Meinten Sie“ row for one term. The field does not change the term itself. */
      suggestion?: NxQuerySuggestion | null;
      /** Opt-in: the „Meinten Sie“ row gets the link „„X“ nicht mehr vorschlagen“ and the field emits `never-suggest`. */
      neverSuggest?: boolean;
      /** The result count text, for the live region: „12 Treffer“. */
      status?: string;
      /** The words that join the members of an any-of group; default the parser's (`ODER`, `oder`, `OR`, `or`). */
      orKeywords?: readonly string[];
      /** The most height the chips and the text take before the field scrolls. */
      maxHeight?: string;
      /** Id of the text field; generated (and tied to a surrounding label) when omitted. */
      id?: string;
      /** Accessible name of the text field when no visible label names it. Default `inputLabel`. */
      ariaLabel?: string;
    }
  >(),
  {
    ...NX_QUERY_INPUT_LABELS_DE,
    interaction: 'direct',
    wording: 'logic',
    fieldChips: () => [],
    clearable: true,
    suggestion: null,
    neverSuggest: false,
    status: '',
    orKeywords: undefined,
    maxHeight: '9em',
    id: undefined,
    ariaLabel: undefined,
  }
);

const terms = defineModel<TextQueryTerm[]>({ default: () => [] });
const draft = defineModel<string>('draft', { default: '' });

const emit = defineEmits<{
  /** The issues of a parse — an empty list when everything could be searched. */
  issues: [issues: ParseIssue[]];
  'accept-suggestion': [suggestion: NxQuerySuggestion];
  'ignore-suggestion': [answer: NxQueryIgnoreSuggestion];
  /** Stop suggesting `text` (only with `neverSuggest`); the same binding as `accept-suggestion`. */
  'never-suggest': [suggestion: NxQuerySuggestion];
  'remove-field': [id: string];
  /** The terms and the draft were cleared through the × (`update:modelValue` is `[]` too). */
  clear: [];
}>();

// Plain text input: stays out of browser autofill in a provideBrowserAutofillOff() scope.
const autofillAttrs = useBrowserAutofillOffAttrs()('text', undefined);

const uid = useId();
const inputId = props.id ?? `${uid}-text`;
const listId = `${uid}-terms`;
const labelInjection = inject(labelInjectionKey, null);
if (labelInjection && !labelInjection.for.value) labelInjection.setFor(inputId);
// A visible label that names the field leaves the aria-label out.
const fieldName = computed(
  () =>
    props.ariaLabel ?? (labelInjection?.for.value === inputId ? undefined : labels.value.inputLabel)
);

const labels = computed<NxQueryInputTexts>(
  () =>
    Object.fromEntries(
      NX_QUERY_INPUT_LABEL_KEYS.map((key) => [key, props[key]])
    ) as unknown as NxQueryInputTexts
);

const rootRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
/** The chip the keys act on (`aria-activedescendant`), or none: the focus is in the text. */
const selectedId = ref<string | null>(null);
/** The chip whose menu is open, or none. */
const menuTermId = ref<string | null>(null);
/** What the last parse could not search, and the minus readings it made. */
const issues = ref<ParseIssue[]>([]);
const readings = ref<MinusReading[]>([]);

// ── What is shown ────────────────────────────────────────────────────────

type Entry =
  | { kind: 'chip'; key: string; term: TextQueryTerm }
  | { kind: 'open' | 'join' | 'close'; key: string; text: string };

/**
 * The Vue key of a chip. The chips and the framing of the any-of group are the children of one
 * list, and a term id is the caller's: any text, `any-close` and `marker:close` too. The chips
 * therefore live under `term:` and the framing under `marker:` — no id can make one the other.
 */
const chipKey = (term: TextQueryTerm): string => `term:${term.id}`;

/**
 * The chips in the order they are shown. The terms of the any-of group stand
 * together, framed „eins von ( … oder … )“, where the first of them stands; the
 * others keep the order of the model.
 */
const entries = computed<Entry[]>(() => {
  const group = terms.value.filter((term) => term.occur === 'should');
  const out: Entry[] = [];
  let grouped = false;
  for (const term of terms.value) {
    if (term.occur !== 'should') {
      out.push({ kind: 'chip', key: chipKey(term), term });
    } else if (!grouped) {
      grouped = true;
      out.push({ kind: 'open', key: 'marker:open', text: labels.value.anyOfOpenLabel });
      group.forEach((member, index) => {
        if (index > 0) {
          out.push({
            kind: 'join',
            key: `marker:join:${member.id}`,
            text: labels.value.anyOfJoinLabel,
          });
        }
        out.push({ kind: 'chip', key: chipKey(member), term: member });
      });
      out.push({ kind: 'close', key: 'marker:close', text: labels.value.anyOfCloseLabel });
    }
  }
  return out;
});

/** The terms in the order they are shown: the order the keys walk through. */
const order = computed(() =>
  entries.value.flatMap((entry) => (entry.kind === 'chip' ? [entry.term] : []))
);
const selectedTerm = computed(
  () => order.value.find((term) => term.id === selectedId.value) ?? null
);

/**
 * The DOM id of a term's option. A term id is the caller's: any text. Every character but
 * letters, digits, `_` and `-` is written as `.<hex>.`, so no two term ids end in one DOM id
 * (`a b` and `a_b` stay apart), and `aria-activedescendant` names the right option.
 */
function optionId(term: TextQueryTerm): string {
  const part = term.id.replace(
    /[^A-Za-z0-9_-]/gu,
    (char) => `.${char.codePointAt(0)!.toString(16)}.`
  );
  return `${uid}-term-${part}`;
}

const issueLines = computed(() => issues.value.map((issue) => issueText(labels.value, issue)));

// A reading holds while the term is still the exclusion it was made for.
const readingLines = computed(() =>
  readings.value.flatMap((reading) => {
    const term = terms.value.find((candidate) => candidate.id === reading.termId);
    if (!term || term.occur !== 'mustNot' || reading.alternative.text !== `-${term.text}`)
      return [];
    return [
      {
        termId: term.id,
        text: fillTemplate(labels.value.minusReadingLabel, {
          occurrence: occurText(labels.value, props.wording, 'mustNot'),
          term: term.text,
        }),
        flip: fillTemplate(labels.value.minusFlipLabel, { alternative: reading.alternative.text }),
      },
    ];
  })
);

const suggestionTerm = computed(() =>
  props.suggestion
    ? (terms.value.find((term) => term.id === props.suggestion?.termId) ?? null)
    : null
);
const hasNotes = computed(
  () =>
    issueLines.value.length > 0 || readingLines.value.length > 0 || suggestionTerm.value !== null
);

// ── Parsing ──────────────────────────────────────────────────────────────

/** The last number of the `tq-<n>` ids this field has made. */
let idCounter = 0;

/**
 * Ids for the terms of one parse: `tq-<n>`, none that a term of the model holds (a saved query
 * can hold any of them) and none that this parse has made. Two terms with one id would be
 * selected, changed and removed together.
 */
function idFactory(held: readonly TextQueryTerm[]): () => string {
  const taken = new Set(held.map((term) => term.id));
  return () => {
    let id = `tq-${(idCounter += 1)}`;
    while (taken.has(id)) id = `tq-${(idCounter += 1)}`;
    taken.add(id);
    return id;
  };
}

/** Turns text into terms: they are appended, what could not be searched stays in the draft. */
function commit(text: string) {
  const result = parse(text, {
    orKeywords: props.orKeywords,
    createId: idFactory(terms.value),
    maxTerms: Math.max(0, MAX_TERMS - terms.value.length),
  });
  issues.value = result.issues;
  readings.value = result.minusReadings;
  selectedId.value = null;
  if (result.terms.length > 0) terms.value = [...terms.value, ...result.terms];
  draft.value = result.rest;
  emit('issues', result.issues);
}

/** Said once after a clear, then dropped so a later count does not repeat it. */
const clearedNote = ref('');

// An empty draft has nothing left to explain: the issues were about text that is gone.
// Typing again retires the „Suche geleert“ notice; clearing itself leaves the draft empty.
watch(draft, (text) => {
  if (text !== '') clearedNote.value = '';
  if (trimWhiteSpace(text) !== '' || issues.value.length === 0) return;
  issues.value = [];
  emit('issues', []);
});

watch(terms, (list) => {
  if (list.length > 0) clearedNote.value = '';
});

// ── An input method ──────────────────────────────────────────────────────

// An input method owns Enter (and a paste) while it composes, and for a moment after
// compositionend (Safari delivers the confirming Enter after it).
const JUST_COMPOSED_MS = 80;
let composing = false;
let justComposed = false;
let justComposedTimer: ReturnType<typeof setTimeout> | undefined;
function onCompositionStart() {
  composing = true;
}
function onCompositionEnd() {
  composing = false;
  justComposed = true;
  if (justComposedTimer !== undefined) clearTimeout(justComposedTimer);
  justComposedTimer = setTimeout(() => {
    justComposed = false;
    justComposedTimer = undefined;
  }, JUST_COMPOSED_MS);
}

function onInput(event: Event) {
  draft.value = (event.target as HTMLInputElement).value;
  selectedId.value = null;
}

// A click, a tap or a press moves the caret into the text without an input event: the person is
// in the text now, and the keys are its again (Backspace deletes a letter, `a` types). A menu
// that was open for a chip goes.
function onTextPointer() {
  selectedId.value = null;
  menuTermId.value = null;
}

/** White space or a wildcard: a whole query, not a word. */
function isQuery(text: string): boolean {
  return hasWhiteSpace(text) || /[*?]/.test(text);
}

function onPaste(event: ClipboardEvent) {
  // While an input method composes (and for a moment after) the text belongs to it: the paste is
  // inserted like any other, never split into chips.
  if (composing || justComposed) return;
  const pasted = event.clipboardData?.getData('text') ?? '';
  if (!isQuery(pasted)) return;
  event.preventDefault();
  const input = event.currentTarget as HTMLInputElement;
  const start = input.selectionStart ?? input.value.length;
  const end = input.selectionEnd ?? start;
  commit(`${input.value.slice(0, start)}${pasted}${input.value.slice(end)}`);
}

// ── Selecting and changing chips ─────────────────────────────────────────

function focusInput() {
  inputRef.value?.focus();
}

function select(id: string | null) {
  selectedId.value = id;
  const term = order.value.find((candidate) => candidate.id === id);
  if (!term) return;
  // The flow scrolls: keep the chip the keys act on in view.
  const domId = optionId(term);
  void nextTick(() => {
    const element = document.getElementById(domId);
    if (element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  });
}

function move(delta: -1 | 1) {
  const at = order.value.findIndex((term) => term.id === selectedId.value);
  if (at < 0) return;
  const next = at + delta;
  if (next < 0) return;
  // Past the last chip: back to the text.
  if (next >= order.value.length) selectedId.value = null;
  else select(order.value[next]!.id);
}

function change(id: string, edit: (term: TextQueryTerm) => TextQueryTerm) {
  let changed = false;
  const next = terms.value.map((term) => {
    if (term.id !== id) return term;
    const edited = edit(term);
    if (edited !== term) changed = true;
    return edited;
  });
  if (changed) terms.value = next;
}

const setOccur = (id: string, occur: Occur) => change(id, (term) => withOccur(term, occur));
const setMatch = (id: string, match: Match) => change(id, (term) => withMatch(term, match));
const setCaseSensitive = (id: string, on: boolean) =>
  change(id, (term) => withCaseSensitive(term, on));
const setDisabled = (id: string, on: boolean) => change(id, (term) => withDisabled(term, on));

function removeTerm(id: string) {
  terms.value = terms.value.filter((term) => term.id !== id);
  if (selectedId.value === id) selectedId.value = null;
  if (menuTermId.value === id) menuTermId.value = null;
  focusInput();
}

function openMenu(id: string) {
  selectedId.value = id;
  menuTermId.value = id;
}

/** Closes the menu; with `refocus` the focus goes back to the text, else the field is left. */
function closeMenu(refocus: boolean) {
  if (menuTermId.value === null) return;
  menuTermId.value = null;
  if (!refocus) {
    selectedId.value = null;
    return;
  }
  void nextTick(() => {
    // Only when the focus is still ours: a click on another control keeps what it took.
    // The open menu counts: it is teleported out of the root.
    const active = document.activeElement;
    if (!active || active === document.body || isOurs(active)) focusInput();
  });
}

// A chip that is gone takes its selection and its menu with it.
watch(order, (list) => {
  if (selectedId.value !== null && !list.some((term) => term.id === selectedId.value)) {
    selectedId.value = null;
  }
  if (menuTermId.value !== null && !list.some((term) => term.id === menuTermId.value)) {
    menuTermId.value = null;
  }
});

// ── Keeping the focus ────────────────────────────────────────────────────

/**
 * A node of this field. The open menu is teleported out of `rootRef` — it must
 * leave `NxTagField`, which neutralises the input tokens of its children — and
 * marks itself with this field's id, so it is still ours.
 */
function isOurs(node: Node | null): boolean {
  if (!node || !rootRef.value) return false;
  if (rootRef.value.contains(node)) return true;
  const el = node instanceof Element ? node : node.parentElement;
  return el?.closest('[data-nx-query-field]')?.getAttribute('data-nx-query-field') === uid;
}

/** The control of this field (not the text) that had the focus when an update began. */
let heldFocus: Element | null = null;

// A chip whose term joins or leaves the any-of group moves in the DOM. The menu
// is teleported, so the move does not take the panel or the focus with it. A
// chip that goes while its menu has the focus takes that control away: the
// focus then returns to the text, not to the page.
onBeforeUpdate(() => {
  const active = document.activeElement;
  heldFocus = active && active !== inputRef.value && isOurs(active) ? active : null;
});

onUpdated(() => {
  const element = heldFocus;
  heldFocus = null;
  if (!element) return;
  // Only a focus that went nowhere: a control that took it meanwhile keeps it.
  const active = document.activeElement;
  if (active && active !== document.body) return;
  if (element instanceof HTMLElement && element.isConnected) element.focus();
  else focusInput();
});

/** The focus left a chip menu for a control outside the field. */
function onFieldLeft() {
  // The menu may already be closed: an outside press closes it first and keeps
  // the selection so the focus can return to the text, then this focusout
  // follows in the same turn. Leaving the field always drops the selection.
  closeMenu(false);
  selectedId.value = null;
}

function flip(termId: string) {
  const reading = readings.value.find((candidate) => candidate.termId === termId);
  if (!reading) return;
  // Only the text and the occurrence differ; the rest is what the person has set on the chip since.
  change(termId, (term) => ({
    ...term,
    text: reading.alternative.text,
    occur: reading.alternative.occur,
  }));
  // The link is gone with the reading: the focus goes back to the text, not to nowhere.
  focusInput();
}

// ── Keys ─────────────────────────────────────────────────────────────────

function atStart(input: HTMLInputElement): boolean {
  return input.selectionStart === 0 && input.selectionEnd === 0;
}

/** A range of the text is selected, not just a caret in it. */
function hasRange(input: HTMLInputElement): boolean {
  return input.selectionStart !== input.selectionEnd;
}

/** The caret keys a chip takes when they come plain. */
const CHIP_CARET_KEYS = new Set(['arrowleft', 'arrowright', 'home', 'end']);

/** The caret keys no chip takes: in a single line they send the caret to its start or end. */
const LINE_CARET_KEYS = new Set(['arrowup', 'arrowdown', 'pageup', 'pagedown']);

/** The letters that select all, cut, paste, undo and redo in a text field, with Ctrl or Cmd. */
const TEXT_SHORTCUTS = new Set(['a', 'x', 'v', 'z', 'y']);

/**
 * The key works on the text — it selects in it, moves the caret, cuts, pastes, undoes or deletes
 * a word — and is the browser's: Ctrl, Cmd or Alt with it, Shift with a caret key (Shift with a
 * letter only types a capital, which the chip keys take as they are), or a caret key no chip
 * takes.
 */
function worksOnText(event: KeyboardEvent): boolean {
  const key = event.key.toLowerCase();
  const shortcut = event.ctrlKey || event.metaKey;
  const modified = shortcut || event.altKey;
  if (LINE_CARET_KEYS.has(key)) return true;
  if (CHIP_CARET_KEYS.has(key)) return modified || event.shiftKey;
  if (key === 'backspace' || key === 'delete') return modified;
  return shortcut && TEXT_SHORTCUTS.has(key);
}

function onKeydown(event: KeyboardEvent) {
  // The input method owns every key while it composes.
  if (event.isComposing || event.keyCode === 229) return;
  const input = event.currentTarget as HTMLInputElement;
  // A selected chip and a selected range of the text never both have the keys. A range that was
  // selected while a chip was (Ctrl+A, a menu that gave the focus back with the range it held) is
  // what the person sees and means: the chip is let go, and Backspace deletes the text instead of
  // removing a chip behind it.
  if (hasRange(input)) selectedId.value = null;
  const current = selectedTerm.value;

  if (event.key === 'Enter') {
    // Safari sends the Enter that confirmed a composition after compositionend.
    if (justComposed) return;
    if (current) {
      event.preventDefault();
      openMenu(current.id);
    } else if (trimWhiteSpace(draft.value) !== '') {
      event.preventDefault();
      commit(draft.value);
    }
    return;
  }
  // A key that works on the text is the browser's, and the person is in the text now: no chip stays
  // selected, so that the next key (Backspace after Ctrl+A) is the text's too. A field without text
  // has nothing to work on: there the chip keys stay as they are.
  if (current && input.value !== '' && worksOnText(event)) {
    selectedId.value = null;
    return;
  }
  if (event.ctrlKey || event.metaKey || event.altKey) return;

  if (!current) {
    // From the start of the text, Left and Backspace step into the chips.
    if (
      (event.key === 'ArrowLeft' || event.key === 'Backspace') &&
      order.value.length > 0 &&
      atStart(input)
    ) {
      event.preventDefault();
      select(order.value[order.value.length - 1]!.id);
    }
    return;
  }

  const key = event.key.toLowerCase();
  if (key === ' ' || key === 'spacebar') {
    event.preventDefault();
    openMenu(current.id);
  } else if (key === 'arrowleft') {
    event.preventDefault();
    move(-1);
  } else if (key === 'arrowright') {
    event.preventDefault();
    move(1);
  } else if (key === 'home') {
    event.preventDefault();
    select(order.value[0]!.id);
  } else if (key === 'end') {
    event.preventDefault();
    select(order.value[order.value.length - 1]!.id);
  } else if (key === 'backspace' || key === 'delete') {
    event.preventDefault();
    removeTerm(current.id);
  } else if (key === '-') {
    event.preventDefault();
    change(current.id, (term) => withOccur(term, term.occur === 'mustNot' ? 'must' : 'mustNot'));
  } else if (key === 'm') {
    event.preventDefault();
    change(current.id, (term) => withMatch(term, nextMatch(term)));
  } else if (key === 'a') {
    event.preventDefault();
    change(current.id, (term) => withCaseSensitive(term, term.caseSensitive !== true));
  }
}

function onBlur() {
  // A composition that is cut off by leaving the field has no end to wait for.
  composing = false;
  // Leaving the text for the menu keeps the chip; leaving the field does not.
  if (menuTermId.value === null) selectedId.value = null;
}

function onFocusout(event: FocusEvent) {
  const next = event.relatedTarget;
  // No target: a click on something that cannot take the focus. Moving into the
  // teleported menu is still inside the field. A focus that leaves both closes.
  if (!(next instanceof Node) || isOurs(next)) return;
  closeMenu(false);
  selectedId.value = null;
}

// ── Suggestion ───────────────────────────────────────────────────────────

// The row goes when the parent has answered it: the focus goes back to the text, not to nowhere.
function accept() {
  if (!props.suggestion) return;
  const { termId, text, generation } = props.suggestion;
  emit('accept-suggestion', { termId, text, generation });
  focusInput();
}

function neverSuggestWord() {
  if (!props.suggestion) return;
  const { termId, text, generation } = props.suggestion;
  emit('never-suggest', { termId, text, generation });
  focusInput();
}

function ignore(permanent: boolean) {
  if (!props.suggestion) return;
  const { termId, generation } = props.suggestion;
  emit('ignore-suggestion', { termId, generation, permanent });
  focusInput();
}

function removeField(id: string) {
  emit('remove-field', id);
  focusInput();
}

/** Terms, typed text or a caller's field chip: something the × can clear. */
const showClear = computed(
  () =>
    props.clearable && (terms.value.length > 0 || props.fieldChips.length > 0 || draft.value !== '')
);

/**
 * Drops every term and the draft. Field chips belong to the caller, who resets
 * them on `clear`. The × never keeps the focus: a click does not take it
 * (mousedown is prevented) and a key that activates it is sent back to the text.
 */
function clearQuery() {
  if (!showClear.value) return;
  const hadIssues = issues.value.length > 0;
  terms.value = [];
  draft.value = '';
  issues.value = [];
  readings.value = [];
  selectedId.value = null;
  menuTermId.value = null;
  if (hadIssues) emit('issues', []);
  clearedNote.value = labels.value.clearedLabel;
  emit('clear');
  focusInput();
}

// ── The live region ──────────────────────────────────────────────────────

/** Between the reading of a minus and its link; decorative, hidden from screen readers. */
const SEPARATOR = '·';

/** How long the text has to stay the same before it is announced. */
const ANNOUNCE_MS = 300;

function sentences(parts: readonly string[]): string {
  return parts
    .map((part) => part.trim().replace(/\.$/, ''))
    .filter((part) => part !== '')
    .join('. ');
}

const liveText = computed(() =>
  sentences([
    clearedNote.value,
    props.status,
    ...issueLines.value,
    ...readingLines.value.map((line) => `${line.text}, ${line.flip}`),
    ...(props.suggestion && suggestionTerm.value
      ? [`${labels.value.suggestionPrefixLabel} ${props.suggestion.text}`]
      : []),
  ])
);
const announced = ref(liveText.value);
let announceTimer: ReturnType<typeof setTimeout> | undefined;
/**
 * Set when the clear notice has just been said. Dropping it changes `liveText`,
 * and that change must not be announced again — the region already said it, and
 * it keeps saying it until the next real change.
 */
let dropClearedQuietly = false;
// The text is announced when it has stayed the same for a moment, and only if it differs from
// what was announced last: a screen reader hears the settled count once, not every keystroke.
watch(liveText, (text) => {
  if (announceTimer !== undefined) clearTimeout(announceTimer);
  announceTimer = undefined;
  if (dropClearedQuietly) {
    dropClearedQuietly = false;
    return;
  }
  if (text === announced.value) return;
  const hadClear = clearedNote.value !== '';
  announceTimer = setTimeout(() => {
    announced.value = text;
    announceTimer = undefined;
    if (hadClear && clearedNote.value !== '') {
      dropClearedQuietly = true;
      clearedNote.value = '';
    }
  }, ANNOUNCE_MS);
});

onBeforeUnmount(() => {
  if (justComposedTimer !== undefined) clearTimeout(justComposedTimer);
  if (announceTimer !== undefined) clearTimeout(announceTimer);
});

defineExpose({
  /** Focuses the text field. */
  focus: focusInput,
});
</script>

<template>
  <div
    ref="rootRef"
    class="nx-query-input"
    data-testid="nx-query-input"
    :data-nx-query-field="uid"
    @focusout="onFocusout"
  >
    <div class="nx-query-input__box">
      <NxTagField :max-height="maxHeight" class="nx-query-input__field">
        <div
          v-if="terms.length > 0"
          :id="listId"
          class="nx-query-input__terms"
          role="listbox"
          aria-orientation="horizontal"
          :aria-label="labels.termsLabel"
          data-testid="nx-query-terms"
        >
          <template v-for="entry in entries" :key="entry.key">
            <NxQueryChip
              v-if="entry.kind === 'chip'"
              :term="entry.term"
              :option-id="optionId(entry.term)"
              :field-id="uid"
              :interaction="interaction"
              :wording="wording"
              :selected="entry.term.id === selectedId"
              :menu-open="menuTermId === entry.term.id"
              :labels="labels"
              @set-occur="setOccur"
              @set-match="setMatch"
              @set-case="setCaseSensitive"
              @set-disabled="setDisabled"
              @remove="removeTerm"
              @open-menu="openMenu"
              @close-menu="closeMenu(true)"
              @field-left="onFieldLeft"
            />
            <span
              v-else
              class="nx-query-input__any"
              aria-hidden="true"
              :data-testid="`nx-query-any-${entry.kind}`"
            >
              {{ entry.text }}
            </span>
          </template>
        </div>
        <NxChip
          v-for="field in fieldChips"
          :key="field.id"
          size="xs"
          removable
          :remove-label="fillTemplate(labels.removeFieldLabel, { label: field.label })"
          data-testid="nx-query-field-chip"
          @remove="removeField(field.id)"
        >
          {{ field.label }}
        </NxChip>
        <input
          :id="inputId"
          ref="inputRef"
          type="text"
          class="nx-query-input__input"
          :class="{ 'nx-query-input__input--clear': showClear }"
          autocomplete="off"
          v-bind="autofillAttrs"
          role="combobox"
          aria-autocomplete="none"
          :aria-label="fieldName"
          :aria-expanded="terms.length > 0 ? 'true' : 'false'"
          :aria-controls="terms.length > 0 ? listId : undefined"
          :aria-activedescendant="selectedTerm ? optionId(selectedTerm) : undefined"
          :placeholder="terms.length > 0 ? labels.placeholderMore : labels.placeholder"
          :value="draft"
          data-testid="nx-query-text"
          @input="onInput"
          @paste="onPaste"
          @keydown="onKeydown"
          @pointerdown="onTextPointer"
          @mousedown="onTextPointer"
          @click="onTextPointer"
          @compositionstart="onCompositionStart"
          @compositionend="onCompositionEnd"
          @blur="onBlur"
        />
      </NxTagField>
      <span v-if="showClear" class="nx-query-input__clear">
        <NxIconButton
          height="xs"
          :aria-label="labels.clearLabel"
          :title="labels.clearLabel"
          data-testid="nx-query-clear"
          @mousedown.prevent
          @click="clearQuery"
        >
          <DismissIcon filled size="1.25em" color="var(--nxt-brand-bg)" />
        </NxIconButton>
      </span>
    </div>
    <div v-if="hasNotes" class="nx-query-input__notes" data-testid="nx-query-notes">
      <div v-if="issueLines.length > 0" data-testid="nx-query-issues">
        <div
          v-for="(line, index) in issueLines"
          :key="index"
          class="nx-query-input__issue"
          data-testid="nx-query-issue"
        >
          {{ line }}
        </div>
      </div>
      <div
        v-for="reading in readingLines"
        :key="reading.termId"
        class="nx-query-input__reading"
        data-testid="nx-query-reading"
      >
        <span data-testid="nx-query-reading-text">{{ reading.text }}</span>
        <span class="nx-query-input__separator" aria-hidden="true">{{ SEPARATOR }}</span>
        <NxButton variant="link" data-testid="nx-query-reading-flip" @click="flip(reading.termId)">
          {{ reading.flip }}
        </NxButton>
      </div>
      <NxSuggestionRow
        v-if="suggestion && suggestionTerm"
        :text="suggestion.text"
        :original="suggestionTerm.text"
        :prefix-label="labels.suggestionPrefixLabel"
        :keep-label="labels.suggestionKeepLabel"
        :confirm-label="labels.suggestionConfirmLabel"
        :confirmable="suggestion.confirmable !== false"
        :never-suggest="neverSuggest"
        :never-suggest-label="labels.suggestionNeverSuggestLabel"
        @accept="accept"
        @keep="ignore(false)"
        @confirm="ignore(true)"
        @never-suggest="neverSuggestWord"
      />
    </div>
    <div
      class="nx-query-input__live"
      aria-live="polite"
      aria-atomic="true"
      data-testid="nx-query-live"
    >
      {{ announced }}
    </div>
  </div>
</template>

<style scoped>
.nx-query-input {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  min-width: 0;
}
/* The × sits on the field's trailing edge, vertically centred, as on NxSearchInput.
   End padding keeps the caret off it. The button itself is only positioned. */
.nx-query-input__box {
  position: relative;
}
.nx-query-input__clear {
  position: absolute;
  z-index: 1;
  inset-inline-end: var(--nxt-space-2xs);
  top: 50%;
  display: flex;
  transform: translateY(-50%);
}
.nx-query-input__input--clear {
  padding-inline-end: calc(
    var(--nxt-font-size-xs) * var(--nxt-line-height-control) + 2 * var(--nxt-space-xs) + 2 *
      var(--nxt-hairline)
  );
}
/* The chips flow inline with the text field (list semantics stay via role="listbox"). */
.nx-query-input__terms {
  display: contents;
}
.nx-query-input__any {
  color: var(--nxt-fg-subtle);
  font-size: var(--nxt-font-size-xs);
  white-space: nowrap;
}
/* The field around it draws the focus ring (NxTagField :has(input:focus-visible)). */
.nx-query-input__input {
  flex: 1 1 calc(var(--nxt-size-unit) * 6);
  min-width: calc(var(--nxt-size-unit) * 4);
  padding: 0 var(--nxt-space-2xs);
  border: none;
  outline: none;
  background: transparent;
  color: var(--nxt-input-fg);
  font: inherit;
  line-height: var(--nxt-line-height-control);
}
.nx-query-input__input::placeholder {
  color: var(--nxt-input-placeholder);
}
/* `type="search"` only keeps browser autofill out (browserAutofill.ts): no search styling. */
.nx-query-input__input[type='search'] {
  appearance: none;
}
.nx-query-input__input[type='search']::-webkit-search-cancel-button,
.nx-query-input__input[type='search']::-webkit-search-decoration {
  appearance: none;
  display: none;
}
.nx-query-input__notes {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
}
.nx-query-input__issue {
  color: var(--nxt-warning-subtle-fg);
}
.nx-query-input__reading {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--nxt-space-2xs) var(--nxt-space-xs);
  color: var(--nxt-fg-subtle);
}
.nx-query-input__live {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}
</style>
